import React from 'react';
import {
  Watchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import { useStorage } from '@cotera/client/app/etc/storage/provider';
import { toast } from '@cotera/client/app/components/ui';
import {
  downloadFile,
  jsonBatchesToCsv,
} from '@cotera/client/app/components/utils/utils';
import {
  createDataGridViewModel,
  createPatchableRowSource,
  type DataGridCellEdit,
  type DataGridColumnDisplayOptions,
  type DataGridColumnStatsSource,
  type DataGridRowId,
  type PatchableRowSource,
} from '@cotera/client/app/components/ui/data-grid';
import type { DatasetOperationColumnType } from '../dataset-columns';
import type {
  ComposedDuckDbPresentation,
  DuckDbLayerStack,
  DuckDbSourceLayer,
} from '@cotera/client/app/etc/duckdb/layers';
import type { DatasetPreviewGridAdapter } from './adapter';
import { DatasetPreviewGridViewModel } from './grid-view-model';
import type { DatasetRowIdCursor } from './row-id-cursor';
import {
  DATASET_PREVIEW_QUERY_SAMPLE_ROWS,
  type DatasetPreviewQueryResult,
  type DatasetPreviewRow,
  type DatasetPreviewStatus,
} from './types';

const DATASET_PREVIEW_PAGE_SIZE = 200;

/**
 * How long a run waits for the grid before giving up on it.
 *
 * A query dropped for one already in flight never settles, and a caller
 * awaiting the result — an agent mid tool call — would otherwise wait forever.
 * Long enough for a large parquet scan, short enough to stay a timeout.
 */
const RUN_QUERY_SETTLE_TIMEOUT_MS = 60_000;

/**
 * Window for collapsing a burst of patches into one stats refetch per column.
 *
 * Long enough to absorb a workflow fan-out (one event per row) and a full-page
 * repaint, short enough that a header visibly follows a change rather than
 * appearing to lag it.
 */
const STATS_INVALIDATION_DEBOUNCE_MS = 250;

type DatasetPreviewGridSource = {
  viewModel: ReturnType<typeof createDataGridViewModel<DatasetPreviewRow>>;
  /** The row detail the layer stack settled on, or null when there is none. */
  rowDetail: ComposedDuckDbPresentation<DatasetPreviewRow>['rowDetail'];
  rowSource: PatchableRowSource<DatasetPreviewRow>;
  status: ReadonlyWatchable<DatasetPreviewStatus>;
  columnStats: DataGridColumnStatsSource;
  customSelectSql: Watchable<string>;
  /** The free-text term narrowing every column, `''` when there is none. */
  search: Watchable<string>;
  queryError: ReadonlyWatchable<string | null>;
  /** Rows whose live-patched value no longer fits the active sort or filter. */
  driftedRowIds: ReadonlyWatchable<Set<DataGridRowId>>;
  requestColumnStats: (columnId: string) => void;
  applyCellEdit: (edit: DataGridCellEdit) => void;
  saveEdits: (edits: DataGridCellEdit[]) => Promise<void>;
  /** Applies a query and resolves once the grid has settled on its result. */
  runQuery: (query: string) => Promise<DatasetPreviewQueryResult>;
  /** Re-runs the page query and clears {@link driftedRowIds}. */
  refresh: () => void;
  /** Downloads what the grid is showing as a CSV file. */
  downloadCsv: (fileName: string) => Promise<void>;
  /**
   * A cursor over the durable row ids a bulk action should act on.
   *
   * `scope` is what the grid is showing versus the whole dataset, and the
   * difference is the user's own filters — so "run on what I am looking at" and
   * "run on everything" are one call apart. `extraWhereSql` narrows further over
   * any column the source exposes, layers included.
   *
   * The caller owns the cursor and must {@link DatasetRowIdCursor.close} it.
   */
  openRowIdCursor: (input: {
    scope: 'all' | 'filtered';
    extraWhereSql?: string;
  }) => Promise<DatasetRowIdCursor>;
  /** One aggregate row over the same source, for a caller's own summary. */
  aggregate: (input: {
    scope: 'all' | 'filtered';
    selectSql: string;
  }) => Promise<Record<string, unknown>>;
  addRow: () => Promise<void>;
  addColumn: (input: {
    name: string;
    type: DatasetOperationColumnType;
  }) => Promise<void>;
  renameColumn: (input: { oldName: string; newName: string }) => Promise<void>;
  deleteColumn: (input: { name: string }) => Promise<void>;
  deleteRows: (rowIds: readonly DataGridRowId[]) => Promise<void>;
};

export const useDatasetPreviewGridSource = (
  adapter: DatasetPreviewGridAdapter,
  layers: DuckDbLayerStack<DatasetPreviewRow>,
  options: {
    /** Only for the dev-mode identity check; the stack is already composed. */
    layerList?: readonly DuckDbSourceLayer<DatasetPreviewRow>[];
    readOnly?: boolean;
    /**
     * The display options saved on the dataset, pulled once when the view model
     * is built to seed the grid. Both this and the sink below must be stable —
     * they flow into the memo that mints the view model, so a new identity
     * rebuilds it and resets sorts, selection and expansion.
     */
    getInitialColumnDisplayOptions?: () => readonly {
      columnName: string;
      displayOptions: DataGridColumnDisplayOptions;
    }[];
    /** Where display-option changes go. Omitted turns persistence off. */
    onColumnDisplayOptionsChange?: (
      columns: {
        columnName: string;
        displayOptions: DataGridColumnDisplayOptions;
      }[]
    ) => void;
  } = {}
): DatasetPreviewGridSource => {
  const storage = useStorage();
  const readOnly = options.readOnly === true;
  const { getInitialColumnDisplayOptions, onColumnDisplayOptionsChange } =
    options;

  const previewViewModel = React.useMemo(
    () =>
      new DatasetPreviewGridViewModel(adapter, layers, {
        storage,
        readOnly,
        getInitialColumnDisplayOptions,
        onColumnDisplayOptionsChange,
      }),
    [
      adapter,
      layers,
      readOnly,
      storage,
      getInitialColumnDisplayOptions,
      onColumnDisplayOptionsChange,
    ]
  );

  /**
   * Subscriptions and timers live for exactly as long as the mount does.
   *
   * Constructing the view model in a memo and connecting it here — rather than
   * subscribing during construction — is what survives StrictMode's
   * mount/unmount/remount: the remount reconnects the same object instead of
   * finding one whose subscriptions have already been torn down.
   */
  React.useEffect(() => previewViewModel.connect(), [previewViewModel]);

  /**
   * Replacing a layer throws the grid away: the memo above mints a fresh view
   * model, so columns, sorts, filters, selection and expansion all reset. It is
   * meant to happen once, when a layer first resolves.
   *
   * The failure is silent — the grid simply looks like it reloaded — and the
   * usual cause is transient UI state (a button's busy flag) captured in the
   * layer's own memo. Naming the layer, rather than just reporting that
   * *something* changed, is the difference between a two-minute fix and an
   * afternoon.
   */
  const previousLayers = React.useRef<Map<string, unknown> | null>(null);
  const layerList = options.layerList;
  React.useEffect(() => {
    if (process.env['NODE_ENV'] === 'production' || layerList === undefined) {
      return;
    }
    const current = new Map(layerList.map((layer) => [layer.id, layer]));
    const previous = previousLayers.current;
    previousLayers.current = current;
    if (previous === null) {
      return;
    }
    const moved = [...current]
      .filter(([id, layer]) => previous.has(id) && previous.get(id) !== layer)
      .map(([id]) => id);
    if (moved.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `Dataset preview layer identity changed (${moved.join(', ')}), which ` +
          'rebuilds the grid view model and resets sorts, selection and ' +
          'expansion. Keep the layer memo stable; hold transient UI state in ' +
          'the component that renders it.'
      );
    }
  }, [layerList]);

  /**
   * The other half of the same check: the adapter is memoized on too, so a
   * fresh adapter for the *same* artifact resets the grid exactly as a moved
   * layer does.
   *
   * Worth its own warning because the cause is always upstream and never
   * visible from here — an unstable dependency of the adapter's memo, several
   * components away — and the symptom is a grid that silently resets itself at
   * intervals nobody can reproduce on demand. `key` is the artifact's identity,
   * so a change to the object without a change to the key is the bug.
   */
  const previousAdapter = React.useRef<DatasetPreviewGridAdapter | null>(null);
  React.useEffect(() => {
    if (process.env['NODE_ENV'] === 'production') {
      return;
    }
    const previous = previousAdapter.current;
    previousAdapter.current = adapter;
    if (
      previous !== null &&
      previous !== adapter &&
      previous.key === adapter.key
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `Dataset preview adapter identity changed for the same artifact ` +
          `(${adapter.key}), which rebuilds the grid view model and resets ` +
          'sorts, filters, selection, scroll and expansion. Something in its ' +
          'memo is unstable across renders.'
      );
    }
  }, [adapter]);
  const {
    grid: viewModel,
    presentation,
    rows,
    totalRows,
    hasMore,
    isLoading,
    status,
    customSelectSql,
    search,
    queryError,
    driftedRowIds,
    columnStats,
    queryParams,
    populationParams,
  } = previewViewModel;
  const generationRef = React.useRef(0);
  // Bumping this both invalidates in-flight stat loads and re-arms every
  // mounted header's "my stats are visible" effect, so only the columns that
  // are actually rendered get reloaded.
  const [statsGeneration, setStatsGeneration] = React.useState(0);
  const statsStateRef = React.useRef({
    generation: 0,
    handled: new Set<string>(),
  });
  /**
   * Callers waiting on the run they asked for.
   *
   * A run is applied by setting a watchable, and the query it triggers happens
   * in a subscriber somewhere else — so "my query finished" is not something
   * `runQuery` can await directly. Each waiter records the query generation it
   * was registered behind and is only resolved by a later one, so a query
   * already in flight when the caller asked cannot answer for it.
   */
  const runQueryWaiters = React.useRef<
    {
      afterGeneration: number;
      resolve: (result: DatasetPreviewQueryResult) => void;
    }[]
  >([]);

  const settleRunQueryWaiters = React.useCallback(
    (generation: number) => {
      const ready = runQueryWaiters.current.filter(
        (waiter) => waiter.afterGeneration < generation
      );
      if (ready.length === 0) {
        return;
      }
      runQueryWaiters.current = runQueryWaiters.current.filter(
        (waiter) => waiter.afterGeneration >= generation
      );
      const loadedRows = rows.snapshot();
      const result: DatasetPreviewQueryResult = {
        rowCount: totalRows.snapshot(),
        columns: Object.keys(loadedRows[0] ?? {}).filter(
          (name) => !name.startsWith('__cotera')
        ),
        sampleRows: loadedRows
          .slice(0, DATASET_PREVIEW_QUERY_SAMPLE_ROWS)
          .map(({ __coteraRowId: _rowId, ...data }) => data),
        error: queryError.snapshot(),
      };
      for (const waiter of ready) {
        waiter.resolve(result);
      }
    },
    [queryError, rows, totalRows]
  );

  const queryPage = React.useCallback(
    async ({
      reset,
      rewind = false,
    }: {
      reset: boolean;
      /**
       * Whether this read replaced the population rather than refreshing it.
       *
       * A sort, a filter, a search or a new statement all give back a different
       * set of rows, in which the reader's scroll offset means nothing — so the
       * body goes back to the top, once and deliberately, instead of being
       * clamped somewhere arbitrary when the content turns out to be shorter
       * than where they were standing.
       *
       * A refresh and an edit re-read the same population, where that offset is
       * exactly where they were reading, so those leave it alone.
       */
      rewind?: boolean;
    }) => {
      if (isLoading.snapshot()) {
        return;
      }
      const generation = ++generationRef.current;
      // Before the query rather than after its rows land: the height changes
      // when the new rows arrive, and moving first means the reader sees one
      // move instead of a move and then a lurch.
      if (rewind) {
        viewModel.rewind();
      }
      isLoading.set(true);
      // Rows deliberately stay mounted while the replacement query runs; the
      // grid shows a progress bar instead of tearing down columns.
      const offset = reset ? 0 : rows.snapshot().length;
      const {
        sorts,
        filters,
        search: searchTerm,
        customSelectSql: customSql,
      } = queryParams.snapshot();
      const page = await adapter.loadPage({
        offset,
        limit: DATASET_PREVIEW_PAGE_SIZE,
        sorts,
        filters,
        search: searchTerm,
        customSelectSql: customSql,
      });
      if (generation !== generationRef.current) {
        return;
      }
      const nextRows = reset ? page.rows : [...rows.snapshot(), ...page.rows];
      rows.set(nextRows);
      if (reset) {
        // The arrangement is true again as of this query, however it was
        // triggered — a sort, a filter, or the refresh chip.
        driftedRowIds.set(new Set());
      }
      totalRows.set(page.totalRows);
      hasMore.set(
        page.totalRows === null
          ? page.rows.length === DATASET_PREVIEW_PAGE_SIZE
          : nextRows.length < page.totalRows
      );
      queryError.set(null);
      isLoading.set(false);
      status.set('ready');
      if (reset) {
        settleRunQueryWaiters(generation);
      }
    },
    [
      adapter,
      driftedRowIds,
      hasMore,
      isLoading,
      queryError,
      queryParams,
      rows,
      settleRunQueryWaiters,
      status,
      totalRows,
      viewModel,
    ]
  );

  const handleQueryFailure = React.useCallback(
    (error: unknown) => {
      // An invalid ad-hoc query empties the grid once, rather than clearing
      // rows first and then failing.
      rows.set([]);
      totalRows.set(0);
      hasMore.set(false);
      queryError.set(
        error instanceof Error ? error.message : 'Could not run query'
      );
      isLoading.set(false);
      status.set('ready');
      settleRunQueryWaiters(generationRef.current);
    },
    [
      hasMore,
      isLoading,
      queryError,
      rows,
      settleRunQueryWaiters,
      status,
      totalRows,
    ]
  );

  const requestColumnStats = React.useCallback(
    (columnId: string) => {
      const state = statsStateRef.current;
      if (state.handled.has(columnId)) {
        return;
      }
      state.handled.add(columnId);
      const generation = statsGeneration;
      const {
        filters,
        search: searchTerm,
        customSelectSql: customSql,
      } = populationParams.snapshot();
      // Stale-while-revalidate: the previous distribution stays on screen until
      // the new one lands, and only a header with nothing to show blanks to
      // "Loading…".
      //
      // A live run reloads these on every burst of patches, and announcing each
      // reload made the header flash — the breakdown collapsed to a single line
      // of placeholder text and re-expanded a moment later, so the column
      // twitched continuously while runs landed. The numbers are a second stale
      // at worst, which is invisible; the flashing was not.
      const shown = columnStats.get(columnId).snapshot();
      if (shown === undefined || shown.kind === 'error') {
        columnStats.set(columnId, { kind: 'loading' });
      }
      void adapter
        .loadColumnStats({
          columnId,
          filters,
          search: searchTerm,
          customSelectSql: customSql,
        })
        .then((stats) => {
          if (statsStateRef.current.generation === generation) {
            columnStats.set(columnId, stats);
          }
        })
        .catch((error: unknown) => {
          // Surfaced rather than swallowed: a blank header is indistinguishable
          // from "no stats yet" and hides real query failures.
          // eslint-disable-next-line no-console
          console.error(`Could not load stats for column ${columnId}`, error);
          if (statsStateRef.current.generation === generation) {
            columnStats.set(columnId, {
              kind: 'error',
              message:
                error instanceof Error ? error.message : 'Could not load stats',
            });
            statsStateRef.current.handled.delete(columnId);
          }
        });
    },
    [adapter, columnStats, populationParams, statsGeneration]
  );

  /**
   * Runs a read now, or as soon as the one in flight is done.
   *
   * `queryPage` drops a request outright while another is running, which is
   * right for the page it is protecting — two concurrent reads of the same
   * source race to set the same rows. What it is not right for is a request
   * that carries *different* params: a sort, a filter or a search typed while
   * the first page was still loading was dropped and never asked again, so the
   * grid went on showing rows the controls no longer described.
   *
   * `runQuery` already worked around this for itself. This is that workaround
   * put where every caller gets it.
   *
   * One wait at a time. Params are read fresh when the query finally runs, so a
   * second change while a wait is armed is already accounted for, and arming a
   * second would only queue a duplicate.
   */
  const waitingToQuery = React.useRef(false);
  const queryPageSoon = React.useCallback(
    (input: { reset: boolean; rewind?: boolean }) => {
      if (!isLoading.snapshot()) {
        void queryPage(input).catch(handleQueryFailure);
        return;
      }
      if (waitingToQuery.current) {
        return;
      }
      waitingToQuery.current = true;
      const unsubscribe = isLoading.subscribe((loading) => {
        if (loading) {
          return;
        }
        unsubscribe();
        waitingToQuery.current = false;
        void queryPage(input).catch(handleQueryFailure);
      });
    },
    [handleQueryFailure, isLoading, queryPage]
  );

  React.useEffect(() => {
    rows.set([]);
    totalRows.set(null);
    hasMore.set(false);
    viewModel.setTotalLoadedRows(0);
    viewModel.setTotalRows(null);
    // A different artifact underneath is as new a population as a filter is.
    void queryPage({ reset: true, rewind: true }).catch(handleQueryFailure);
  }, [
    adapter.key,
    handleQueryFailure,
    hasMore,
    queryPage,
    rows,
    totalRows,
    viewModel,
  ]);

  const rowSource = React.useMemo(
    () =>
      createPatchableRowSource<DatasetPreviewRow>({
        getRowId: adapter.getRowId,
        rows,
        totalRows,
        hasMore,
        isLoading,
        loadMore() {
          return queryPage({ reset: false });
        },
      }),
    [adapter.getRowId, hasMore, isLoading, queryPage, rows, totalRows]
  );

  React.useEffect(
    () =>
      queryParams.subscribe(() => {
        // Sorts, filters, the search box and the ad-hoc statement: every one of
        // them hands back a different set of rows.
        queryPageSoon({ reset: true, rewind: true });
      }),
    [queryPageSoon, queryParams]
  );

  // Filters and ad-hoc queries change which rows exist, so cached stats are
  // stale. Sorting does not, and deliberately does not invalidate them.
  React.useEffect(
    () =>
      populationParams.subscribe(() => {
        const generation = statsStateRef.current.generation + 1;
        statsStateRef.current = { generation, handled: new Set() };
        columnStats.clear();
        // Rendered headers re-request through their own effect once
        // `requestColumnStats` changes identity, so off-screen columns stay
        // unloaded.
        setStatsGeneration(generation);
      }),
    [columnStats, populationParams]
  );

  const invalidateColumnStats = React.useCallback(
    (columnId: string) => {
      statsStateRef.current.handled.delete(columnId);
      if (viewModel.headersExpanded.snapshot()) {
        // Deliberately not cleared first. The reload keeps whatever is on
        // screen until its replacement arrives, and clearing here would put the
        // flash back — one blank frame before the request even starts.
        requestColumnStats(columnId);
        return;
      }
      // Nothing is rendering it, so drop it and let the next expand reload.
      columnStats.set(columnId, undefined);
    },
    [columnStats, requestColumnStats, viewModel]
  );

  /**
   * Coalesced form of {@link invalidateColumnStats}, for changes that arrive in
   * bursts rather than one at a time.
   *
   * A live run fans out one event per row, and a reconcile repaints every
   * loaded row at once. Invalidating per patch would fire a DuckDB aggregate
   * per event for the same column, so ids accumulate and flush together on a
   * trailing timer — the same shape as the settings persistence above.
   *
   * A patch also carries values that are not columns at all: the overlay's
   * per-workflow timestamps and run ids ride on the row for the detail panel
   * to read. Those have no header and no stats, so anything the view model
   * does not know about is dropped rather than queried for.
   */
  const statsInvalidationTimer = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const pendingStatsColumns = React.useRef<Set<string>>(new Set());

  const flushStatsInvalidation = React.useCallback(() => {
    statsInvalidationTimer.current = null;
    const pending = [...pendingStatsColumns.current];
    pendingStatsColumns.current = new Set();
    const known = new Set(
      viewModel.columns.snapshot().map((column) => column.id)
    );
    for (const columnId of pending) {
      if (known.has(columnId)) {
        invalidateColumnStats(columnId);
      }
    }
  }, [invalidateColumnStats, viewModel]);

  const queueStatsInvalidation = React.useCallback(
    (columnId: string) => {
      pendingStatsColumns.current.add(columnId);
      if (statsInvalidationTimer.current !== null) {
        clearTimeout(statsInvalidationTimer.current);
      }
      statsInvalidationTimer.current = setTimeout(
        flushStatsInvalidation,
        STATS_INVALIDATION_DEBOUNCE_MS
      );
    },
    [flushStatsInvalidation]
  );

  React.useEffect(
    () => () => {
      if (statsInvalidationTimer.current !== null) {
        clearTimeout(statsInvalidationTimer.current);
      }
    },
    []
  );

  // The layer stack's live channel. Values are patched into loaded rows one
  // cell at a time rather than re-running the page query: a re-query would
  // resort the grid under whatever the user is looking at.
  //
  // Always registered — the composed subscribe fans out to whichever layers
  // asked for one, and is a no-op teardown when none did.
  React.useEffect(() => {
    return presentation.subscribe({
      patch: (rowId, values) => {
        const { sorts, filters, search: searchTerm } = queryParams.snapshot();
        // Read once, before anything is applied: a layer that reconciles by
        // restating every loaded row sends mostly values the row already holds,
        // and acting on those costs a re-render plus a stats reload per column
        // to redisplay what is already on screen. The header distributions were
        // the visible half of that.
        const current = rows
          .snapshot()
          .find((row) => adapter.getRowId(row) === rowId);
        let drifted = false;
        for (const [columnId, value] of Object.entries(values)) {
          if (
            current !== undefined &&
            Object.is((current as Record<string, unknown>)[columnId], value)
          ) {
            continue;
          }
          rowSource.applyPatch({
            type: 'update-cell',
            rowId,
            columnId,
            value,
          });
          // The header describes a population this value just moved within.
          queueStatsInvalidation(columnId);
          // Same test `applyCellEdit` uses to decide a re-query is needed —
          // but this change was not user-initiated, so it is reported rather
          // than acted on.
          // A search names no column, so any changed value can have moved the
          // row in or out of it — which is why an active one drifts the row
          // whatever was patched.
          drifted =
            drifted ||
            searchTerm !== '' ||
            sorts.some((sort) => sort.columnId === columnId) ||
            filters.some((filter) => filter.columnId === columnId);
        }
        if (drifted) {
          const next = new Set(driftedRowIds.snapshot());
          next.add(rowId);
          driftedRowIds.set(next);
        }
      },
      insertRow: (values) => {
        // `__coteraRowId` is only ever consulted for a dataset with no durable
        // id column, and such a dataset has nothing to address this row by in
        // the first place — no layer can produce one. Same reasoning as
        // `adapter.addRow`, which fills it the same way.
        const row = { ...values, __coteraRowId: -1 };
        rowSource.applyPatch({ type: 'insert-row', row });
        // A new row joins every column's population, so every distribution now
        // describes one row fewer than exists.
        for (const column of viewModel.columns.snapshot()) {
          queueStatsInvalidation(column.id);
        }
        // Appended, because where the row belongs is decided by a query this
        // grid is not re-running. That is only actually the right place when
        // the loaded rows are the whole result in its natural order — so an
        // active sort or filter, or a page that has more to load, all mean the
        // position is a guess and the row is reported as drifted. Refreshing is
        // what puts it where it goes.
        const { sorts, filters, search: searchTerm } = queryParams.snapshot();
        if (
          sorts.length > 0 ||
          filters.length > 0 ||
          searchTerm !== '' ||
          hasMore.snapshot()
        ) {
          const next = new Set(driftedRowIds.snapshot());
          next.add(adapter.getRowId(row));
          driftedRowIds.set(next);
        }
      },
      deleteRow: (rowId) => {
        rowSource.applyPatch({ type: 'delete-row', rowId });
        for (const column of viewModel.columns.snapshot()) {
          queueStatsInvalidation(column.id);
        }
        // Deliberately not reported as drift: the row is gone under every sort
        // and filter, so the page is more true for having dropped it, and a
        // "rows changed" chip naming a row nobody can see is just noise.
        const drifted = driftedRowIds.snapshot();
        if (drifted.has(rowId)) {
          const next = new Set(drifted);
          next.delete(rowId);
          driftedRowIds.set(next);
        }
      },
      loadedRowIds: () => rows.snapshot().map((row) => adapter.getRowId(row)),
    });
  }, [
    adapter,
    driftedRowIds,
    hasMore,
    presentation,
    queryParams,
    queueStatsInvalidation,
    rowSource,
    rows,
    viewModel,
  ]);

  /**
   * Writes an edit through to DuckDB so sorting, filtering and stats all see
   * it. Rows are patched in place first so the cell updates immediately; the
   * page is only re-queried when the edited column takes part in the active
   * sort or filters — or when a search is running, which every column takes
   * part in — since that is when row order or membership can change.
   */
  const applyCellEdit = React.useCallback(
    (edit: DataGridCellEdit) => {
      void adapter
        .applyCellEdit({
          rowId: edit.rowId,
          columnName: edit.columnId,
          value: edit.value,
        })
        .then(() => {
          rowSource.applyPatch({
            type: 'update-cell',
            rowId: edit.rowId,
            columnId: edit.columnId,
            value: edit.value,
          });
          invalidateColumnStats(edit.columnId);
          const { sorts, filters, search: searchTerm } = queryParams.snapshot();
          // The search is column-agnostic, so every edit is one it might have
          // an opinion about — there is no column to test against.
          const affectsQuery =
            searchTerm !== '' ||
            sorts.some((sort) => sort.columnId === edit.columnId) ||
            filters.some((filter) => filter.columnId === edit.columnId);
          if (affectsQuery) {
            void queryPage({ reset: true }).catch(handleQueryFailure);
          }
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Could not apply cell edit', error);
          toast.error('Could not apply edit');
        });
    },
    [
      adapter,
      handleQueryFailure,
      invalidateColumnStats,
      queryPage,
      queryParams,
      rowSource,
    ]
  );

  const saveEdits = React.useCallback(
    async (edits: DataGridCellEdit[]) => {
      await adapter.saveCellEdits(edits);
    },
    [adapter]
  );

  /**
   * Running a query applies it to the grid but does not persist it — that is
   * the Save action's job, so an edited query reads as unsaved until then.
   */
  const runQuery = React.useCallback(
    (query: string): Promise<DatasetPreviewQueryResult> => {
      const afterGeneration = generationRef.current;
      const settled = new Promise<DatasetPreviewQueryResult>((resolve) => {
        const waiter = {
          afterGeneration,
          resolve: (result: DatasetPreviewQueryResult): void => {
            clearTimeout(timeout);
            resolve(result);
          },
        };
        const timeout = setTimeout(() => {
          runQueryWaiters.current = runQueryWaiters.current.filter(
            (candidate) => candidate !== waiter
          );
          resolve({
            rowCount: null,
            columns: [],
            sampleRows: [],
            error: 'The query did not finish in time.',
          });
        }, RUN_QUERY_SETTLE_TIMEOUT_MS);
        runQueryWaiters.current.push(waiter);
      });

      customSelectSql.set(query);
      adapter.onQueryChanged(query);

      // Setting the query is normally what runs it, via the subscriber on
      // `queryParams`. Two cases where that does not happen: re-running the
      // query already applied changes nothing to subscribe to, and `queryPage`
      // drops a request outright while another is in flight. Both look
      // identical from here — the generation never moved — and both would
      // otherwise leave the caller waiting on a query that is never going to
      // run. A large file still loading makes the second case ordinary, not an
      // edge.
      if (generationRef.current !== afterGeneration) {
        return settled;
      }
      if (!isLoading.snapshot()) {
        void queryPage({ reset: true }).catch(handleQueryFailure);
        return settled;
      }
      const unsubscribe = isLoading.subscribe((loading) => {
        if (loading) {
          return;
        }
        unsubscribe();
        void queryPage({ reset: true }).catch(handleQueryFailure);
      });
      return settled;
    },
    [adapter, customSelectSql, handleQueryFailure, isLoading, queryPage]
  );

  /**
   * Adding a row goes straight to the operation log and is patched into the
   * loaded rows, so the new row appears at the bottom without re-running the
   * query and losing the user's place.
   */
  const addRow = React.useCallback(async () => {
    const row = await adapter.addRow();
    rowSource.applyPatch({ type: 'insert-row', row });
    // Put the cursor in the row that was just asked for. Appended at the bottom
    // of a grid taller than the viewport, an empty row is invisible — the
    // button read as doing nothing, and the usual answer was to press it again.
    // The grid scrolls whatever it focuses into view, so this is also what
    // brings the row on screen.
    const rowIndex = rowSource.rows
      .snapshot()
      .findIndex(
        (candidate) => adapter.getRowId(candidate) === adapter.getRowId(row)
      );
    if (rowIndex !== -1) {
      viewModel.focusFirstEditableCell(adapter.getRowId(row), rowIndex);
    }
    // A new row joins every column's population, so every distribution is now
    // describing one row fewer than exists.
    for (const column of viewModel.columns.snapshot()) {
      queueStatsInvalidation(column.id);
    }
    // Appended, because where the row belongs is decided by a query this grid is
    // not re-running. That is only actually the right place when the loaded rows
    // are the whole result in its natural order — so an active sort or filter, or
    // a page that has more to load, all mean the position is a guess and the row
    // is reported as drifted. Same reasoning as the live `insertRow` path.
    const { sorts, filters, search: searchTerm } = queryParams.snapshot();
    if (
      sorts.length > 0 ||
      filters.length > 0 ||
      searchTerm !== '' ||
      hasMore.snapshot()
    ) {
      const next = new Set(driftedRowIds.snapshot());
      next.add(adapter.getRowId(row));
      driftedRowIds.set(next);
    }
  }, [
    adapter,
    driftedRowIds,
    hasMore,
    queryParams,
    queueStatsInvalidation,
    rowSource,
    viewModel,
  ]);

  /**
   * Deleting rows goes straight to the operation log and is patched out of the
   * loaded rows, so they disappear without re-running the query and losing the
   * user's place — the same treatment {@link addRow} gets.
   */
  const deleteRows = React.useCallback(
    async (rowIds: readonly DataGridRowId[]) => {
      await adapter.deleteRows({ rowIds });
      for (const rowId of rowIds) {
        rowSource.applyPatch({ type: 'delete-row', rowId });
      }
      // The deleted rows were part of every column's population, so every
      // distribution now describes more rows than exist.
      for (const column of viewModel.columns.snapshot()) {
        queueStatsInvalidation(column.id);
      }
      // Deliberately not reported as drift, for the reason the live `deleteRow`
      // path gives: a row that is gone under every sort and filter makes the
      // page more true, not less, and a "rows changed" chip naming rows nobody
      // can see is only noise. Any drift already recorded against them goes.
      const drifted = driftedRowIds.snapshot();
      const stale = rowIds.filter((rowId) => drifted.has(rowId));
      if (stale.length > 0) {
        const next = new Set(drifted);
        for (const rowId of stale) {
          next.delete(rowId);
        }
        driftedRowIds.set(next);
      }
    },
    [adapter, driftedRowIds, queueStatsInvalidation, rowSource, viewModel]
  );

  /**
   * A new column changes the schema every read is built from, so the adapter is
   * rebuilt by the page rather than patched here.
   */
  const addColumn = React.useCallback(
    async (input: { name: string; type: DatasetOperationColumnType }) => {
      await adapter.addColumn(input);
    },
    [adapter]
  );

  /**
   * A rename changes the name every read addresses the column by, so — like
   * {@link addColumn} — the adapter is rebuilt by the page rather than patched
   * here.
   */
  const renameColumn = React.useCallback(
    async (input: { oldName: string; newName: string }) => {
      // Ahead of the write, not after it: the adapter reports the rename
      // synchronously, which rebuilds the view model — and the rebuild reads
      // the saved layout. Moving it afterwards would be moving it too late,
      // with the new grid already built on settings that no longer match any
      // column. Put back if the write does not land.
      DatasetPreviewGridViewModel.renameStoredColumn({
        storage,
        datasetId: adapter.datasetId,
        ...input,
      });
      try {
        await adapter.renameColumn(input);
      } catch (error) {
        DatasetPreviewGridViewModel.renameStoredColumn({
          storage,
          datasetId: adapter.datasetId,
          oldName: input.newName,
          newName: input.oldName,
        });
        throw error;
      }
    },
    [adapter, storage]
  );

  /**
   * Dropping a column changes the schema every read is built from, so — like
   * {@link addColumn} — the adapter is rebuilt by the page rather than patched
   * here.
   */
  const deleteColumn = React.useCallback(
    async (input: { name: string }) => {
      await adapter.deleteColumn(input);
    },
    [adapter]
  );

  /** Re-runs the page query, which is also what clears the drift count. */
  const refresh = React.useCallback(() => {
    void queryPage({ reset: true }).catch(handleQueryFailure);
  }, [handleQueryFailure, queryPage]);

  /**
   * Exports through the same query params the page reads, so the file matches
   * the grid rather than the dataset — a user who filtered before exporting
   * meant the filter.
   */
  const downloadCsv = React.useCallback(
    async (fileName: string) => {
      const toastController = toast.loading('Preparing CSV…');
      try {
        const {
          sorts,
          filters,
          search: searchTerm,
          customSelectSql: customSql,
        } = queryParams.snapshot();
        const cursor = adapter.exportRows({
          sorts,
          filters,
          search: searchTerm,
          customSelectSql: customSql,
        });
        // Serialised page by page: the export never holds the whole dataset in
        // JS, only one page plus the CSV text it has emitted so far.
        const { chunks, rowCount } = await jsonBatchesToCsv(cursor.batches());
        if (rowCount === 0) {
          toastController.error('Nothing to export');
          return;
        }
        downloadFile(chunks, 'csv', fileName);
        toastController.success(`${rowCount.toLocaleString()} rows exported`);
      } catch (error) {
        toastController.error(
          error instanceof Error ? error.message : 'Could not export this CSV'
        );
      }
    },
    [adapter, queryParams]
  );

  /**
   * Reads through the same query params the grid renders from, so "filtered"
   * means exactly the rows on screen rather than a predicate rebuilt beside it.
   */
  const openRowIdCursor = React.useCallback(
    async (input: { scope: 'all' | 'filtered'; extraWhereSql?: string }) => {
      const {
        filters,
        search: searchTerm,
        customSelectSql: customSql,
      } = queryParams.snapshot();
      return adapter.openRowIdCursor({
        // The ad-hoc query rides along even for `all`: it defines what the
        // grid's rows *are*, where a filter only hides some of them. The search
        // hides rather than defines, so it goes the way the filters go.
        customSelectSql: customSql,
        filters: input.scope === 'filtered' ? filters : [],
        search: input.scope === 'filtered' ? searchTerm : '',
        ...(input.extraWhereSql !== undefined && {
          extraWhereSql: input.extraWhereSql,
        }),
      });
    },
    [adapter, queryParams]
  );

  const aggregate = React.useCallback(
    async (input: { scope: 'all' | 'filtered'; selectSql: string }) => {
      const {
        filters,
        search: searchTerm,
        customSelectSql: customSql,
      } = queryParams.snapshot();
      return adapter.aggregate({
        customSelectSql: customSql,
        filters: input.scope === 'filtered' ? filters : [],
        search: input.scope === 'filtered' ? searchTerm : '',
        selectSql: input.selectSql,
      });
    },
    [adapter, queryParams]
  );

  return {
    viewModel,
    rowDetail: presentation.rowDetail,
    rowSource,
    status,
    columnStats,
    customSelectSql,
    search,
    queryError,
    driftedRowIds,
    requestColumnStats,
    applyCellEdit,
    saveEdits,
    runQuery,
    refresh,
    downloadCsv,
    openRowIdCursor,
    aggregate,
    addRow,
    addColumn,
    renameColumn,
    deleteColumn,
    deleteRows,
  };
};
